import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useCallback, useEffect, useState, useRef } from "react";
import { useAccount } from "~/hooks/useAccount";
import { IPlan } from "~/types/billing";
import { usePayment } from "./usePayment";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SetupIntent } from "@stripe/stripe-js";
import { toast } from "~/components/toast";
import { INVOICES, PLANS } from "~/api/billing/types";
import { STORE } from "~/api/store/types";
import { paths } from "~/router/paths";
import _debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import { getCheckoutAction } from "~/helpers/plans";

import { useSendFailureReason } from "~/api/billing/queries";
import { updateSubscription } from "~/api/billing/requests";

export const useStripeConfirmation = (
  plan: IPlan | undefined,
  upgradeWithStore: boolean,
  setIsLoading?: (val: boolean) => void,
  onSuccess?: () => void,
  disable_redirect_on_finish?: boolean
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasProcessedPayment = useRef(false);

  const [isConfirming, setIsConfirming] = useState(
    searchParams.has("setup_intent_client_secret") &&
      searchParams.has("setup_intent_client_secret") &&
      searchParams.get("redirect_status") === "succeeded"
  );

  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { refetch } = useAccount();

  const { getAccount, account } = useAccount();
  const checkoutActionType = getCheckoutAction(account, plan);

  // Capture the initial checkout action type so it survives the account refresh
  // race condition. After Stripe redirect, account is null on first render
  // (giving "firstSubscription"/"trial"), but getAccount() resolves before
  // useCallback re-creates the debounced handler, overwriting it to "upgrade".
  const initialCheckoutActionType = useRef(checkoutActionType);

  const { payStripe } = usePayment({
    planId: plan?.id as string,
    isUserShopify: false,
    upgradeWithStore: upgradeWithStore,
  });
  const client = useQueryClient();
  const navigate = useNavigate();
  const sendFailureReasonMutation = useSendFailureReason();

  const handleSuccess = async (plan: IPlan) => {
    // Prevent multiple executions
    if (hasProcessedPayment.current) return;
    hasProcessedPayment.current = true;

    // Stripe confirms the payment client-side before the backend has processed
    // the subscription webhook, so an immediate account refetch can still return
    // the previous plan. Force a backend reconciliation first so getAccount()
    // below returns up-to-date data instead of racing the webhook.
    try {
      await updateSubscription();
    } catch (error) {
      console.error("Failed to reconcile subscription status:", error);
    }

    client.invalidateQueries(PLANS);
    client.invalidateQueries(STORE);
    client.invalidateQueries(INVOICES);
    localStorage.removeItem("onboarding_with_create_store");
    await getAccount();
    onSuccess?.();

    // Clear URL parameters to prevent re-processing but keep plan_id for upgrade funnel
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("setup_intent_client_secret");
    newSearchParams.delete("payment_intent_client_secret");
    newSearchParams.delete("redirect_status");
    newSearchParams.delete("setup_intent");

    // Keep plan_id in URL so ShowUpgradeModal can detect it and open the upgrade funnel
    if (!newSearchParams.has("plan_id") && plan?.id) {
      newSearchParams.set("plan_id", plan.id);
    }

    setSearchParams(newSearchParams, { replace: true });

    if (disable_redirect_on_finish) {
      return;
    }

    const isFirstSubscription =
      initialCheckoutActionType.current == "firstSubscription" ||
      initialCheckoutActionType.current == "trial";

    navigate(
      `${paths.app.home}?plan_id=${plan?.id}&first_subscription=${isFirstSubscription}`
    );
  };

  const commitPayment = async (
    setupIntent: SetupIntent | null = null,
    promoCodeId?: string,
    returnError: boolean = false
  ) => {
    if (!plan || hasProcessedPayment.current) return; // Add check to prevent duplicate processing

    const error = await payStripe(
      setupIntent
        ? { setup_intent: setupIntent.id, promo_code_id: promoCodeId }
        : { promo_code_id: promoCodeId }
    );

    if (error) {
      const failureData =
        typeof error === "string"
          ? { reason: error }
          : {
              reason: error.message ?? String(t("checkout.went_wrong")),
              code: error.code,
              decline_code: error.decline_code,
              message: error.message,
            };
      sendFailureReasonMutation.mutate(failureData);
      if (
        error != "error" &&
        error.type === "invalid_request_error" &&
        error.code === "payment_intent_incompatible_payment_method"
      ) {
        refetch();
      }
      if (returnError) return { error: true };
    } else {
      await handleSuccess(plan);
    }
  };

  const debouncedSetupIntentCheck = useCallback(
    _debounce((setupIntentClientSecret: string, promoCodeId: string | null) => {
      if (!stripe || !plan || hasProcessedPayment.current) return; // Add check here too

      stripe
        .retrieveSetupIntent(setupIntentClientSecret)
        .then(async ({ setupIntent, error }) => {
          if (!setupIntent) {
            toast.warning(error.message ?? t("checkout.went_wrong"));
            return;
          }
          switch (setupIntent.status) {
            case "succeeded":
              if (setIsConfirming) setIsConfirming(true);
              await commitPayment(setupIntent, promoCodeId ?? undefined);
              if (setIsConfirming) setIsConfirming(false);
              break;
            case "processing":
              toast.warning(t("checkout.processing_payment"));
              break;
            case "requires_payment_method":
              toast.error(t("checkout.unsuccessful_payment"));
              navigate(window.location.pathname);
              break;
            default:
              toast.warning(t("checkout.went_wrong"));
              navigate(window.location.pathname);
              break;
          }
        });
    }, 500),
    [stripe, plan]
  );

  const debouncedPaymentIntentCheck = useCallback(
    _debounce((setupIntentClientSecret: string) => {
      if (!stripe || !plan || hasProcessedPayment.current) return; // Add check here too

      stripe
        .retrievePaymentIntent(setupIntentClientSecret)
        .then(async ({ paymentIntent, error }) => {
          if (!paymentIntent) {
            toast.warning(error.message ?? t("checkout.went_wrong"));
            setIsConfirming(false);
            return;
          }
          switch (paymentIntent.status) {
            case "succeeded":
              await handleSuccess(plan);
              break;
            case "processing":
              toast.warning(t("checkout.processing_payment"));
              break;
            case "requires_payment_method":
              toast.error(t("checkout.unsuccessful_payment"));
              navigate(window.location.pathname);
              break;
            default:
              toast.warning(t("checkout.went_wrong"));
              navigate(window.location.pathname);
              break;
          }
          setIsConfirming(false);
        });
    }, 500),
    [stripe, plan]
  );

  useEffect(() => {
    // Reset the flag when plan changes
    hasProcessedPayment.current = false;
  }, [plan?.id]);

  useEffect(() => {
    const setupIntentClientSecret = searchParams.get(
      "setup_intent_client_secret"
    );
    const paymentIntentClientSecret = searchParams.get(
      "payment_intent_client_secret"
    );
    const promoCodeId = searchParams.get("promo_code_id");

    if (!stripe || !plan || hasProcessedPayment.current) return;

    if (setupIntentClientSecret && !paymentIntentClientSecret)
      debouncedSetupIntentCheck(setupIntentClientSecret, promoCodeId);
    if (paymentIntentClientSecret)
      debouncedPaymentIntentCheck(paymentIntentClientSecret);
  }, [stripe, plan]);

  const handlePayment = async (returnError = false) => {
    if (!stripe || !elements || !setIsLoading) {
      return;
    }

    setIsLoading(true);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("plan_id", plan?.id as string);

    let returnURL = `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`;
    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: returnURL,
        },
      });
      if (error) {
        const failureData = {
          reason: error.message ?? String(t("checkout.went_wrong")),
          code: error.code,
          decline_code: error.decline_code,
          message: error.message,
        };
        sendFailureReasonMutation.mutate(failureData);
        toast.error(error.message ?? t("checkout.went_wrong"));
      }
    } catch (err: any) {
      const promoCodeId = searchParams.get("promo_code_id");
      const paymentError = await commitPayment(
        null,
        promoCodeId ?? undefined,
        returnError
      );
      if (returnError && paymentError?.error) return { error: true };
    }
    localStorage.removeItem("isOnboarding");
    setIsLoading(false);
  };

  return { handlePayment, isConfirming };
};
